import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { VendorListItem } from "../../models/ListItem";
import { Property, PropertyFacilityMapping } from "../../models/property.model";
import { PropertyType } from "../../models/propertyType.model";
import { citiesService } from "../../service/cities.service";
import { propertyTypeService } from "../../service/propertyType.service";
// Add PrimeReact imports
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/lara-light-blue/theme.css";
import { TreeTable } from "primereact/treetable";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Amenity, RMCAmenity } from "../../models/amenity.model";
import { City } from "../../models/cities.model";
import { Facility } from "../../models/facility.model";
import { Location } from "../../models/location.model";
import { amenityService } from "../../service/amenity.service";
import { countryService } from "../../service/country.service";
import { facilityService } from "../../service/facilites.service";
import { locationService } from "../../service/location.service";
import { propertyService } from "../../service/property.service";
import { roomTypeService } from "../../service/roomType.service";
import { statesService } from "../../service/state.service";
import { tierService } from "../../service/tier.Service";
import { vendorService } from "../../service/vendor.service";
import LoadingSpinner from "../LoadingSpinner";

type TreeNode = {
  key: string;
  label: string;
  data?: Amenity;
  icon?: string;
  children?: TreeNode[];
};
type FlatTreeNode = {
  key: string;
  label: string;
  data?: Facility;
};
interface AddEditPropertyProps {
  initialData?: Property;
  onSave: (property: Property) => void;
  onCancel: () => void;
}

const defaultForm: Property & {
  roomTypes?: number[];
  facilities?: PropertyFacilityMapping[];
} = {
  name: "",
  description: "",
  propertyTypeId: 0,
  address: "",
  locationId: 0,
  landmark: "",
  cityId: 0,
  zipCode: "",
  vendorId: 0,
  tierId: 0,
  pseudoName: "",
  //creditPeriod: 0,
  totalInventory: 0,
  furnishingType: "",
  buildYear: new Date().getFullYear(),
  brochureLink: "",
  videoLink: "",
  website: "",
  masterPlanLink: "",
  additionalComment: "",
  termsAndConditions: "",
  isActive: true,
  amenities: [],
  roomTypes: [],
  facilities: [],
};
type DropdownOption = { label: string; value: number };
const AddEditProperty: React.FC<Partial<{ initialData: Property }>> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { propertyId: urlPropertyId } = useParams<{ propertyId: string }>();

  // Get property from props or from navigation state or URL params
  const initialData: any | undefined = location.state?.property;
  const propertyId: any | undefined =
    location.state?.propertyId || urlPropertyId;
  // Some navigations may omit mode; infer edit if propertyId present
  const mode = location.state?.mode || (propertyId ? "edit" : "create");
  const [facilitiesList, setFacilitiesList] = useState<Facility[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<{
    [key: string]: { checked: boolean };
  }>({});
  console.log("Initial Data:", JSON.stringify(initialData));
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const [form, setForm] = useState<Property>(() => {
    if (initialData) {
      // Ensure all required fields have default values
      return {
        ...defaultForm,
        ...initialData,
        vendorId: initialData.vendorId || 0,
        tierId: initialData.tierId || 0,
        name: initialData.name || "",
        description: initialData.description || "",
        address: initialData.address || "",
        landmark: initialData.landmark || "",
        zipCode: initialData.zipCode || "",
        pseudoName: initialData.pseudoName || "",
        furnishingType: initialData.furnishingType || "",
        brochureLink: initialData.brochureLink || "",
        videoLink: initialData.videoLink || "",
        website: initialData.website || "",
        masterPlanLink: initialData.masterPlanLink || "",
        additionalComment: initialData.additionalComment || "",
        termsAndConditions: initialData.termsAndConditions || "",
      };
    }
    return defaultForm;
  });
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [states, setStates] = useState<DropdownOption[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [countries, setCountries] = useState<DropdownOption[]>([]);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [rmcAmenities, setRMCAmenities] = useState<RMCAmenity[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<{
    [key: string]: { checked: boolean };
  }>({});
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<number[]>([]);
  const [buildYearError, setBuildYearError] = useState<string>("");
  const [zipCodeError, setZipCodeError] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [pseudoNameError, setPseudoNameError] = useState<string>("");
  const currentYear = new Date().getFullYear();
  const isReadOnly = userRole !== "Administrator";
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [vendors, setVendors] = useState<VendorListItem[]>([]);
  const [tiers, setTiers] = useState<DropdownOption[]>([]);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [deleteImageIds, setDeleteImageIds] = useState<number[]>([]);
  const [mainImageId, setMainImageId] = useState<number | undefined>(undefined); // existing image main
  const [newMainUploadIndex, setNewMainUploadIndex] = useState<
    number | undefined
  >(undefined); // new upload designated main
  const [isLoading, setIsLoading] = useState(false);
  const propertyLoadedRef = useRef<string | null>(null);

  // Memoized expensive calculations to prevent recalculation on every render
  const buildYearOptions = useMemo(() => {
    return Array.from({ length: currentYear - 1900 + 1 }, (_, i) => {
      const year = currentYear - i;
      return { label: year.toString(), value: year };
    });
  }, [currentYear]);

  const facilityNodes: FlatTreeNode[] = useMemo(() => {
    return facilitiesList.map((f) => ({
      key: f.id.toString(),
      label: f.name,
      data: f,
    }));
  }, [facilitiesList]);

  // Memoized tree building function
  const buildTreeFromFlatAmenities = useMemo(
    () =>
      (flatAmenities: Amenity[]): TreeNode[] => {
        const map = new Map<number, TreeNode>();
        const roots: TreeNode[] = [];

        for (const item of flatAmenities) {
          if ((item as any).$ref) continue; // Skip $ref

          const node: TreeNode = {
            key: item.id.toString(),
            label: item.name,
            data: item,
            children: [],
          };

          map.set(item.id, node);
        }

        for (const item of flatAmenities) {
          if ((item as any).$ref) continue;

          const node = map.get(item.id)!;
          if (item.parentAmenityId != null && map.has(item.parentAmenityId)) {
            map.get(item.parentAmenityId)!.children!.push(node);
          } else {
            roots.push(node);
          }
        }

        return roots;
      },
    []
  );
  // Separate useEffect for loading static data (runs once)
  useEffect(() => {
    const loadStaticData = async () => {
      try {
        // Load all static data in parallel to reduce loading time
        const [
          propertyTypesData,
          countriesData,
          facilitiesData,
          amenitiesData,
          roomTypesData,
          vendorsData,
          tiersData,
        ] = await Promise.all([
          propertyTypeService.getAll(),
          countryService.getAll(),
          facilityService.getAll().catch(() => []),
          amenityService.getAll(),
          roomTypeService.getAll(),
          vendorService.getListItems().catch(() => []),
          tierService.getListItems().catch(() => []),
        ]);

        setPropertyTypes(propertyTypesData);
        setCountries(
          countriesData.map((c: any) => ({ label: c.name, value: c.id }))
        );
        setFacilitiesList(facilitiesData);
        setVendors(vendorsData);
        setTiers(tiersData.map((t: any) => ({ label: t.name, value: t.id })));

        if (amenitiesData) {
          const treeData = buildTreeFromFlatAmenities(amenitiesData);
          setNodes(treeData);
        }

        const roomTypesArray = Array.isArray(roomTypesData)
          ? roomTypesData
          : roomTypesData ?? [];
        setRoomTypes(roomTypesArray);
      } catch (error) {
        console.error("Error loading static data:", error);
      }
    };

    loadStaticData();
  }, [buildTreeFromFlatAmenities]); // Include memoized function dependency

  // Populate images immediately if initialData already carries them (avoids empty flicker)
  useEffect(() => {
    if (initialData) {
      const rawImageDtos =
        (initialData as any).ImageDtos?.$values || initialData.ImageDtos;
      const rawPropertyImages =
        (initialData as any).propertyImages?.$values ||
        initialData.propertyImages;
      const imgs: any[] = rawImageDtos || rawPropertyImages || [];
      if (Array.isArray(imgs) && imgs.length) {
        setExistingImages(imgs);
        const currentMain = imgs.find((i) => i.isMainImage);
        if (currentMain) setMainImageId(currentMain.id);
      }
    }
  }, [initialData]);

  // Load property data when editing (propertyId present)
  useEffect(() => {
    if (
      !propertyId ||
      mode !== "edit" ||
      propertyLoadedRef.current === propertyId
    ) {
      return;
    }

    const loadPropertyData = async () => {
      setIsLoading(true);
      propertyLoadedRef.current = propertyId;
      try {
        const response = await propertyService.getById(propertyId);
        if (!response) return;

        // Ensure all form fields have proper default values
        const formData = {
          ...defaultForm,
          ...response,
          vendorId: response.vendorId || 0,
          tierId: response.tierId || 0,
          name: response.name || "",
          description: response.description || "",
          address: response.address || "",
          landmark: response.landmark || "",
          zipCode: response.zipCode || "",
          pseudoName: response.pseudoName || "",
          furnishingType: response.furnishingType || "",
          brochureLink: response.brochureLink || "",
          videoLink: response.videoLink || "",
          website: response.website || "",
          masterPlanLink: response.masterPlanLink || "",
          additionalComment: response.additionalComment || "",
          termsAndConditions: response.termsAndConditions || "",
        };

        setForm(formData);

        const rawImageDtos =
          (response as any).imageDtos?.$values ||
          (response as any).imageDtos ||
          (response as any).ImageDtos?.$values ||
          (response as any).ImageDtos;
        const rawPropertyImages =
          (response as any).propertyImages?.$values ||
          (response as any).propertyImages;
        let imgs: any[] = rawImageDtos || rawPropertyImages || [];
        if (!Array.isArray(imgs)) imgs = [];
        setExistingImages(imgs);
        const currentMain = imgs.find((i) => i.isMainImage);
        if (currentMain) setMainImageId(currentMain.id);

        // Handle room types
        if (Array.isArray(response.roomTypes)) {
          setSelectedRoomTypes(response.roomTypes.map((rt) => rt.id));
        } else if ((response as any).roomTypes?.$values) {
          setSelectedRoomTypes(
            (response as any).roomTypes.$values.map((rt: any) => rt.id)
          );
        }

        // Handle amenities
        const amenitiesArr: Amenity[] =
          (response.amenities as any)?.$values ??
          (response.amenities as any) ??
          [];
        const selectedAmenityMap: { [key: string]: { checked: boolean } } = {};
        amenitiesArr.forEach((a) => {
          if (a?.id != null)
            selectedAmenityMap[a.id.toString()] = { checked: true };
          if (a?.parentAmenityId != null)
            selectedAmenityMap[a.parentAmenityId.toString()] = {
              checked: true,
            };
        });
        setSelectedAmenities(selectedAmenityMap);

        // Handle facilities
        const facilitiesArr: PropertyFacilityMapping[] =
          (response.facilities as any)?.$values ??
          (response.facilities as any) ??
          [];
        const selectedFacilitiesMap: { [key: string]: { checked: boolean } } =
          {};
        facilitiesArr.forEach((f) => {
          if (f?.facilityId != null)
            selectedFacilitiesMap[f.facilityId.toString()] = { checked: true };
        });
        setSelectedFacilities(selectedFacilitiesMap);

        // Load location data in sequence to avoid duplicate calls
        if (response.countryId) {
          setSelectedCountry(response.countryId);
          const statesData = await statesService.getByCountryId(
            response.countryId
          );
          setStates(
            statesData.map((s: any) => ({ label: s.name, value: s.id }))
          );

          if (response.stateId) {
            setSelectedState(response.stateId);
            const citiesData = await citiesService.getByStateId(
              response.stateId
            );
            setCities(citiesData ?? []);

            if (response.cityId) {
              setSelectedCity(response.cityId);
              const locationsData = await locationService.getByCityId(
                response.cityId
              );
              setLocations(locationsData ?? []);
            }
          }
        }
      } catch (error) {
        console.error("Error loading property data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPropertyData();
  }, [mode, propertyId]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    let fieldValue: string | number | boolean = value;

    if (name === "zipCode") {
      // Only allow numbers
      if (!/^\d*$/.test(value)) {
        setZipCodeError("Zip code must contain only numbers.");
        return;
      } else {
        setZipCodeError("");
      }
    }
    if (name === "name") {
      setNameError("");
    }
    if (name === "pseudoName") {
      setPseudoNameError("");
    }

    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      fieldValue = e.target.checked;
    } else if (
      [
        "propertyTypeId",
        "cityId",
       // "creditPeriod",
        "totalInventory",
        "buildYear",
      ].includes(name)
    ) {
      fieldValue = Number(value);
    }
    setForm((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
  };

  const handleSave = async (property: Property) => {
    let patched = { ...property };

    // Validate Name/PseudoName based on vendor's iosOwned status
    if (patched.vendorId) {
      const selectedVendor = vendors.find((v) => v.id === patched.vendorId);
      const isIOSOwned = selectedVendor?.iosOwned || false;

      if (isIOSOwned) {
        // IOS-owned properties require Name
        if (!patched.name?.trim()) {
          Swal.fire({
            icon: "warning",
            title: "Property Name Required",
            text: "Property name is required for IOS-owned properties.",
          });
          setNameError("Property name is required for IOS owned properties");
          return;
        }
      } else {
        // Non-IOS-owned properties require PseudoName
        if (!patched.pseudoName?.trim()) {
          Swal.fire({
            icon: "warning",
            title: "Pseudo Name Required",
            text: "Pseudo name is required for non-IOS-owned properties.",
          });
          setPseudoNameError(
            "Pseudo name is required for non-IOS owned properties"
          );
          return;
        }
        // For non-IOS owned, copy PseudoName to Name to satisfy backend validation
        // Backend will also normalize it, but we send it to avoid 400 error
        if (!patched.name?.trim()) {
          patched.name = patched.pseudoName;
        }
      }
    }

    if (!patched.vendorId && vendors.length > 0) {
      patched.vendorId = Number(vendors[0].id);
      console.warn(
        "No vendor selected, defaulting to first vendor:",
        vendors[0]
      );
    }
    if (!patched.vendorId) {
      Swal.fire({
        icon: "warning",
        title: "Vendor is required",
        text: "Please select a vendor before saving.",
      });
      return;
    }

    if (!patched.tierId && tiers.length > 0) {
      patched.tierId = Number(tiers[0].value);
      console.warn("No tier selected, defaulting to first tier:", tiers[0]);
    }
    if (!patched.tierId) {
      Swal.fire({
        icon: "warning",
        title: "Tier is required",
        text: "Please select a tier before saving.",
      });
      return;
    }
    console.log("Saving property with vendorId:", patched.vendorId);
    console.log("Saving property with tierId:", patched.tierId);
    console.log("Full property object being saved:", patched);
    if (patched.id) {
      const updated = await propertyService.updateProperty(patched.id, patched);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Property updated successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } else {
      const created = await propertyService.createProperty(patched);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Property created successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
    setTimeout(() => {
      navigate(-1);
    }, 500);
  };
  // TODO: Add UI to show existing images with delete & set-main actions, collecting DeleteImageIds & MainImageId into the form submission.
  const handleCancel = () => {
    navigate(-1);
  };
  const validateBuildYear = (year: number | null | undefined): boolean => {
    if (typeof year !== "number" || isNaN(year)) return false;
    return /^\d{4}$/.test(year.toString());
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "edit" && existingImages.length === 0) {
      console.debug(
        "[AddEditProperty] No existing images detected at submit. form.id:",
        form.id,
        "propertyId param:",
        propertyId
      );
    }
    if (!validateBuildYear(form.buildYear)) {
      setBuildYearError(
        "Please enter a valid year in YYYY format (e.g., 2025)."
      );
      return;
    } else {
      setBuildYearError("");
    }
    // Prevent: deleting all existing images without adding replacements (edit mode only)
    if (mode === "edit") {
      const remainingExisting = existingImages.filter(
        (img) => !deleteImageIds.includes(img.id)
      );
      const newUploads = form.uploadedFiles?.length || 0;
      if (
        existingImages.length > 0 &&
        remainingExisting.length === 0 &&
        newUploads === 0
      ) {
        Swal.fire({
          icon: "warning",
          title: "At least one image required",
          text: "You removed all existing images. Please upload at least one new image before saving.",
          confirmButtonText: "OK",
        });
        return;
      }
      // Ensure a main image will remain (either among remaining existing or new uploads where first new becomes main implicitly backend side)
      const remainingWithMain = remainingExisting.some(
        (i) => i.isMainImage && !deleteImageIds.includes(i.id)
      );
      if (!remainingWithMain) {
        // If user uploaded new files, we'll let backend assign first new as main; else require user to pick one
        if (newUploads === 0) {
          Swal.fire({
            icon: "warning",
            title: "Select a main image",
            text: "Please choose a main image before saving.",
            confirmButtonText: "OK",
          });
          return;
        }
      }
    }
    const selectedAmenityIds = Object.keys(selectedAmenities).filter(
      (k) => selectedAmenities[k]?.checked
    );
    const selectedFacilityIds = Object.keys(selectedFacilities).filter(
      (k) => selectedFacilities[k]?.checked
    );
    const selectedFacilityObjects: PropertyFacilityMapping[] =
      selectedFacilityIds
        .filter((id) => id !== "facility-root")
        .map((id) => {
          const facility = facilitiesList.find((f) => f.id === +id);
          return {
            id: 0,
            propertyId: form.id ?? 0,
            facilityId: facility?.id ?? 0,
            name: facility?.name ?? "",
          };
        });
    const selectedAmenityObjects: Amenity[] = selectedAmenityIds
      .map((id) => {
        let amenity = amenities.find((a) => a.id === Number(id));
        if (!amenity) {
          const findInTree = (nodes: TreeNode[]): Amenity | undefined => {
            for (const node of nodes) {
              if (node.data?.id === Number(id)) return node.data;
              if (node.children) {
                const found = findInTree(node.children);
                if (found) return found;
              }
            }
            return undefined;
          };
          amenity = findInTree(nodes);
        }
        return amenity;
      })
      .filter(Boolean)
      .map((a) => ({
        id: a!.id,
        name: a!.name,
        description: a!.description ?? "",
        iconName: a!.iconName ?? "",
        parentAmenityId: a!.parentAmenityId ?? null,
        childAmenities: {
          $values: Array.isArray(a!.childAmenities)
            ? a!.childAmenities
            : a!.childAmenities?.$values ?? [],
        },
      }));
    const selectedRoomTypeObjects = selectedRoomTypes.map((id) => {
      const rt = roomTypes.find((rt) => rt.id === id);
      return {
        id: rt?.id,
        name: rt?.name,
        description: rt?.description ?? "",
      };
    });

    const updatedForm = {
      ...form,
      amenities: selectedAmenityObjects,
      roomTypes: selectedRoomTypeObjects,
      facilities: selectedFacilityObjects,
    };
    (updatedForm as any).deleteImageIds = deleteImageIds;
    (updatedForm as any).mainImageId = mainImageId;
    (updatedForm as any).newMainUploadIndex = newMainUploadIndex;
    handleSave(updatedForm);
  };
  const handleCountryChange = useCallback(async (countryId: number) => {
    setSelectedCountry(countryId);
    setSelectedState(null);
    setSelectedCity(null);
    setCities([]);
    setStates([]);
    setLocations([]);

    if (!countryId || countryId === 0) return;

    try {
      const result = await statesService.getByCountryId(countryId);
      setStates(result.map((s: any) => ({ label: s.name, value: s.id })));
    } catch (error) {
      console.error("Error loading states:", error);
    }
  }, []);

  const handleStateChange = useCallback(async (stateId: number) => {
    setSelectedState(stateId);
    setSelectedCity(null);
    setCities([]);
    setLocations([]);

    if (!stateId || stateId === 0) return;

    setListLoading(true);
    try {
      const result = await citiesService.getByStateId(stateId);
      setCities(result ?? []);
    } catch {
      setCities([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const handleCityChange = useCallback(async (cityId: number) => {
    setSelectedCity(cityId);
    setLocations([]);

    if (!cityId || cityId === 0) return;

    setListLoading(true);
    try {
      const result = await locationService.getByCityId(cityId);
      setLocations(result ?? []);
    } catch {
      setLocations([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  // Optimized form field handlers to prevent unnecessary re-renders
  const handlePropertyTypeChange = useCallback((value: any) => {
    setForm((prev) => ({ ...prev, propertyTypeId: value }));
  }, []);

  const handleVendorChange = useCallback((value: any) => {
    setForm((prev) => ({ ...prev, vendorId: value }));
    // Clear name/pseudoName errors when vendor changes
    setNameError("");
    setPseudoNameError("");
  }, []);

  const handleTierChange = useCallback((value: any) => {
    setForm((prev) => ({ ...prev, tierId: value }));
  }, []);

  const handleLocationChange = useCallback((value: any) => {
    setForm((prev) => ({ ...prev, locationId: value }));
  }, []);

  const handleIsActiveChange = useCallback((value: any) => {
    setForm((prev) => ({ ...prev, isActive: value }));
  }, []);

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <LoadingSpinner size="lg" text="Loading property..." />
      </div>
    );
  }

  return (
    <>
      <div className="content">
        <div className="content-header">
          <div className="container d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <h6 className="_600 mb-2 page-heading">
                {userRole === "Administrator"
                  ? propertyId
                    ? "Edit Property"
                    : "Add Property"
                  : "View Property"}
              </h6>
            </div>
          </div>
        </div>
        <div className="content-body">
          <div className="container">
            <div className="row">
              <div className="col-md-12 mb-3">
                <div className="card mb-5">
                  <div className="card-body">
                    <Form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-8 mb-3">
                          <h4 className="mb-3 tx-16 _600">
                            Primary Information
                          </h4>

                          <div className="row">
                            <div className="col-md-6 form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="vendor">Vendor *</label>
                                <Dropdown
                                  disabled={isReadOnly}
                                  id="vendorId"
                                  name="vendorId"
                                  value={form.vendorId || 0}
                                  options={vendors}
                                  onChange={(e) => handleVendorChange(e.value)}
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder="Select Vendor"
                                  className="w-100"
                                  appendTo="self"
                                  showClear
                                />
                              </div>
                            </div>

                            <div className="col-md-6 form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="name">
                                  {vendors.find((v) => v.id === form.vendorId)
                                    ?.iosOwned
                                    ? "Property Name *"
                                    : "Property Name"}
                                </label>
                                <InputText
                                  disabled={isReadOnly}
                                  id="name"
                                  name="name"
                                  placeholder="Enter property name"
                                  value={form.name || ""}
                                  onChange={handleChange}
                                />
                                {nameError && (
                                  <small className="text-danger">
                                    {nameError}
                                  </small>
                                )}
                              </div>
                            </div>

                            <div className="col-md-6 form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="pseudoName">
                                  {vendors.find((v) => v.id === form.vendorId)
                                    ?.iosOwned
                                    ? "Pseudo Name"
                                    : "Pseudo Name *"}
                                </label>
                                <InputText
                                  disabled={isReadOnly}
                                  id="pseudoName"
                                  name="pseudoName"
                                  placeholder="Enter pseudo name"
                                  value={form.pseudoName || ""}
                                  onChange={handleChange}
                                />
                                {pseudoNameError && (
                                  <small className="text-danger">
                                    {pseudoNameError}
                                  </small>
                                )}
                              </div>
                            </div>

                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="type">Type</label>
                                <Dropdown
                                  disabled={isReadOnly}
                                  id="propertyTypeId"
                                  name="propertyTypeId"
                                  value={form.propertyTypeId || 0}
                                  options={propertyTypes}
                                  onChange={(e) =>
                                    handlePropertyTypeChange(e.value)
                                  }
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder="Select Type"
                                  className="w-100"
                                  required
                                  appendTo="self"
                                />
                              </div>
                            </div>

                            <div className="col-md-6 form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="roomTypes">Room Type</label>
                                <MultiSelect
                                  disabled={isReadOnly}
                                  id="roomTypes"
                                  name="roomTypes"
                                  value={selectedRoomTypes}
                                  options={roomTypes}
                                  onChange={(e) =>
                                    setSelectedRoomTypes(e.value)
                                  }
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder="Select Room Types"
                                  display="chip"
                                  className="w-100"
                                  appendTo="self"
                                />
                              </div>
                            </div>

                            <div className="col-md-6 form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="tier">Tier</label>
                                <Dropdown
                                  disabled={isReadOnly}
                                  id="tierId"
                                  name="tierId"
                                  value={form.tierId || 0}
                                  options={tiers}
                                  onChange={(e) => handleTierChange(e.value)}
                                  optionLabel="label"
                                  optionValue="value"
                                  placeholder="Select Tier"
                                  className="w-100"
                                  appendTo="self"
                                  showClear
                                />
                              </div>
                            </div>
                            <div className="col-md-12  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="type">Description</label>
                                <InputTextarea
                                  disabled={isReadOnly}
                                  rows={3}
                                  name="description"
                                  value={form.description || ""}
                                  onChange={handleChange}
                                  placeholder="Description"
                                />
                              </div>
                            </div>
                          </div>
                          <h4 className="mb-3 tx-16 _600">
                            Location Information
                          </h4>
                          <div className="row">
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="country">Country</label>
                                <Dropdown
                                  value={selectedCountry}
                                  options={countries}
                                  onChange={(e) => {
                                    setForm((prev) => ({
                                      ...prev,
                                      countryId: e.value,
                                    }));
                                    handleCountryChange(e.value);
                                  }}
                                  placeholder="Select Country"
                                  className="w-auto"
                                  style={{ minWidth: 180 }}
                                  appendTo="self"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="state">State</label>
                                <Dropdown
                                  disabled={!selectedCountry || isReadOnly}
                                  value={selectedState}
                                  options={states}
                                  onChange={(e) => {
                                    setForm((prev) => ({
                                      ...prev,
                                      stateId: e.value,
                                    }));
                                    handleStateChange(e.value);
                                  }}
                                  placeholder="Select State"
                                  className="w-auto"
                                  style={{ minWidth: 180 }}
                                  appendTo="self"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="landmark">City</label>
                                <Dropdown
                                  disabled={!selectedState || isReadOnly}
                                  id="cityId"
                                  name="cityId"
                                  value={form.cityId || 0}
                                  options={cities}
                                  onChange={(e) => {
                                    setSelectedCity(e.value);
                                    setForm((prev) => ({
                                      ...prev,
                                      cityId: e.value,
                                    }));
                                    handleCityChange(e.value);
                                  }}
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder="Select City"
                                  className="w-100"
                                  required
                                  appendTo="self"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="location">Location</label>
                                <Dropdown
                                  disabled={!selectedCity || isReadOnly}
                                  id="locationId"
                                  name="locationId"
                                  value={form.locationId || 0}
                                  options={locations}
                                  onChange={(e) =>
                                    handleLocationChange(e.value)
                                  }
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder="Select Location"
                                  className="w-100"
                                  required
                                  appendTo="self"
                                />
                              </div>
                            </div>
                            <div className="col-md-12  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="name">Address</label>
                                <InputText
                                  disabled={isReadOnly}
                                  id="address"
                                  name="address"
                                  value={form.address || ""}
                                  onChange={handleChange}
                                  placeholder="Address"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="landmark">Landmark</label>
                                <InputText
                                  disabled={isReadOnly}
                                  name="landmark"
                                  value={form.landmark || ""}
                                  onChange={handleChange}
                                  placeholder="Landmark"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="zipcode">Zipcode</label>
                                <InputText
                                  disabled={isReadOnly}
                                  id="zipcode"
                                  name="zipCode"
                                  value={form.zipCode || ""}
                                  onChange={handleChange}
                                  placeholder="Zip Code"
                                  inputMode="numeric"
                                  pattern="\d*"
                                />
                                {zipCodeError && (
                                  <small className="text-danger">
                                    {zipCodeError}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                          <h4 className="mb-3 tx-16 _600">
                            Property Information
                          </h4>
                          <div className="row">
                            {/* <div className='col-md-6  form-group'>
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="creditperiod">Credit Period</label>
                                <InputNumber disabled={isReadOnly} name="creditPeriod" value={form.creditPeriod || 0} onValueChange={e => setForm(prev => ({ ...prev, creditPeriod: e.value ?? 0 }))} placeholder="Enter Credit Period Value" />
                              </div>
                            </div> */}
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="totalInventory">
                                  Total Inventory
                                </label>
                                <InputNumber
                                  disabled={isReadOnly}
                                  name="totalInventory"
                                  value={form.totalInventory || 0}
                                  onValueChange={(e) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      totalInventory: e.value ?? 0,
                                    }))
                                  }
                                  placeholder="Enter Total Inventory Value"
                                />
                              </div>
                            </div>
                            {/* <div className='col-md-6  form-group'>
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="furnishingType">Furnishing Type</label>
                                <InputText disabled={isReadOnly} name="furnishingType" value={form.furnishingType || ''} onChange={handleChange} placeholder="Furnishing Type" />

                              </div>
                            </div> */}
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="buildYear">Build Year</label>
                                <Dropdown
                                  id="buildYear"
                                  name="buildYear"
                                  disabled={isReadOnly}
                                  value={
                                    form.buildYear || new Date().getFullYear()
                                  }
                                  options={buildYearOptions}
                                  onChange={(e) => {
                                    setForm((prev) => ({
                                      ...prev,
                                      buildYear: e.value,
                                    }));
                                    setBuildYearError("");
                                  }}
                                  placeholder="Select Build Year"
                                  className="w-100"
                                  required
                                  appendTo="self"
                                />
                                {buildYearError && (
                                  <small className="text-danger">
                                    {buildYearError}
                                  </small>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="brochureLink">
                                  Brochure Link
                                </label>
                                <InputText
                                  disabled={isReadOnly}
                                  name="brochureLink"
                                  value={form.brochureLink || ""}
                                  onChange={handleChange}
                                  placeholder="Add Brochure Link"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="videoLink">Video Link</label>
                                <InputText
                                  disabled={isReadOnly}
                                  id="videoLink"
                                  name="videoLink"
                                  value={form.videoLink || ""}
                                  onChange={handleChange}
                                  placeholder="Enter Video Link"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="website">Website</label>
                                <InputText
                                  disabled={isReadOnly}
                                  id="website"
                                  name="website"
                                  value={form.website || ""}
                                  onChange={handleChange}
                                  placeholder="Enter Website"
                                />
                              </div>
                            </div>
                            <div className="col-md-6  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="masterPlanLink">
                                  Master Plan Link
                                </label>
                                <InputText
                                  disabled={isReadOnly}
                                  id="masterPlanLink"
                                  name="masterPlanLink"
                                  value={form.masterPlanLink || ""}
                                  onChange={handleChange}
                                  placeholder="Add Master Plan Link"
                                />
                              </div>
                            </div>
                            <div className="col-md-6 form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="images">Property Images</label>
                                <input
                                  type="file"
                                  name="images"
                                  id="images"
                                  multiple
                                  accept="image/*"
                                  className="form-control"
                                  disabled={isReadOnly}
                                  onChange={(e) =>
                                    setForm({
                                      ...form,
                                      uploadedFiles: Array.from(
                                        e.target.files || []
                                      ),
                                    })
                                  }
                                />
                              </div>
                            </div>
                            {mode === "edit" && existingImages.length > 0 && (
                              <div className="col-md-12 mt-3">
                                <label>Existing Images</label>
                                <div className="d-flex flex-wrap gap-3">
                                  {existingImages
                                    .filter(
                                      (img) => !deleteImageIds.includes(img.id)
                                    )
                                    .map((img) => {
                                      const isMain =
                                        mainImageId === img.id ||
                                        (img.isMainImage &&
                                          mainImageId === undefined);
                                      const displayUrl =
                                        img.imageUrl ||
                                        (img.fileName
                                          ? /^https?:/i.test(img.fileName)
                                            ? img.fileName
                                            : `/Images/${img.fileName}`
                                          : "");
                                      return (
                                        <div
                                          key={img.id}
                                          style={{ width: 150 }}
                                          className="border p-2 position-relative rounded bg-light"
                                        >
                                          {isMain && (
                                            <span
                                              style={{
                                                position: "absolute",
                                                top: 4,
                                                left: 4,
                                                background: "#0d6efd",
                                                color: "#fff",
                                                fontSize: 10,
                                                padding: "2px 6px",
                                                borderRadius: 12,
                                              }}
                                            >
                                              MAIN
                                            </span>
                                          )}
                                          <img
                                            src={displayUrl}
                                            alt={img.fileName}
                                            style={{
                                              width: "100%",
                                              height: 100,
                                              objectFit: "cover",
                                              borderRadius: 4,
                                              background: "#eee",
                                            }}
                                          />
                                          <div className="form-check mt-1 d-flex align-items-center gap-1">
                                            <input
                                              className="form-check-input"
                                              type="radio"
                                              name="mainImage"
                                              disabled={isReadOnly}
                                              checked={mainImageId === img.id}
                                              onChange={() => {
                                                setMainImageId(img.id);
                                                setNewMainUploadIndex(
                                                  undefined
                                                );
                                              }}
                                            />
                                            <label
                                              className="form-check-label"
                                              style={{ fontSize: 12 }}
                                            >
                                              Set Main
                                            </label>
                                          </div>
                                          {!isReadOnly && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger w-100 mt-1"
                                              onClick={() =>
                                                setDeleteImageIds((prev) => [
                                                  ...prev,
                                                  img.id,
                                                ])
                                              }
                                            >
                                              Delete
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                                {deleteImageIds.length > 0 && (
                                  <small className="text-warning d-block mt-2">
                                    Marked {deleteImageIds.length} image(s) for
                                    deletion
                                  </small>
                                )}
                              </div>
                            )}
                            {form.uploadedFiles &&
                              form.uploadedFiles.length > 0 && (
                                <div className="col-md-12 mt-3">
                                  <label>New Uploads</label>
                                  <div className="d-flex flex-wrap gap-3">
                                    {form.uploadedFiles.map((file, idx) => {
                                      const isChosenMain =
                                        newMainUploadIndex === idx ||
                                        (newMainUploadIndex == null &&
                                          idx === 0 &&
                                          mainImageId == null);
                                      return (
                                        <div
                                          key={file.name + idx}
                                          style={{ width: 170 }}
                                          className="border p-2 position-relative rounded bg-white"
                                        >
                                          {isChosenMain && (
                                            <span
                                              style={{
                                                position: "absolute",
                                                top: 4,
                                                left: 4,
                                                background: "#198754",
                                                color: "#fff",
                                                fontSize: 10,
                                                padding: "2px 6px",
                                                borderRadius: 12,
                                              }}
                                            >
                                              MAIN (new)
                                            </span>
                                          )}
                                          <img
                                            src={URL.createObjectURL(file)}
                                            alt={file.name}
                                            style={{
                                              width: "100%",
                                              height: 100,
                                              objectFit: "cover",
                                              borderRadius: 4,
                                            }}
                                          />
                                          <div
                                            style={{ fontSize: 11 }}
                                            className="mt-1 text-truncate"
                                          >
                                            {file.name}
                                          </div>
                                          <small style={{ fontSize: 10 }}>
                                            {(file.size / 1024).toFixed(1)} KB
                                          </small>
                                          <div className="form-check mt-1 d-flex align-items-center gap-1">
                                            <input
                                              className="form-check-input"
                                              type="radio"
                                              name="newMainUpload"
                                              disabled={isReadOnly}
                                              checked={isChosenMain}
                                              onChange={() => {
                                                setNewMainUploadIndex(idx);
                                                setMainImageId(undefined);
                                              }}
                                            />
                                            <label
                                              className="form-check-label"
                                              style={{ fontSize: 11 }}
                                            >
                                              Set Main
                                            </label>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <small className="text-muted d-block mt-1">
                                    Select a radio button to mark a new upload
                                    as main. If none selected and no existing
                                    main chosen, first new image is main.
                                  </small>
                                </div>
                              )}

                            <div className="col-md-12  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="zipcode">
                                  Additional Comment
                                </label>
                                <InputTextarea
                                  disabled={isReadOnly}
                                  rows={3}
                                  id="additionalComment"
                                  name="additionalComment"
                                  value={form.additionalComment || ""}
                                  onChange={handleChange}
                                  placeholder="Additional Comment"
                                />
                              </div>
                            </div>

                            <div className="col-md-12  form-group">
                              <div className="d-flex flex-column gap-2">
                                <label htmlFor="termsAndConditions">
                                  Terms and Conditions
                                </label>
                                <InputTextarea
                                  disabled={isReadOnly}
                                  rows={3}
                                  id="termsAndConditions"
                                  name="termsAndConditions"
                                  value={form.termsAndConditions || ""}
                                  onChange={handleChange}
                                  placeholder="Terms and Conditions"
                                />
                              </div>
                            </div>
                            <div className="col-md-12  form-group">
                              <div className="d-flex flex-row gap-2">
                                <InputSwitch
                                  disabled={isReadOnly}
                                  id="isActive"
                                  name="isActive"
                                  checked={form.isActive ?? false}
                                  onChange={(e) =>
                                    handleIsActiveChange(e.value)
                                  }
                                />
                                <label htmlFor="isActive">
                                  Property Status
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="ms-md-3 mt-4 mt-md-0">
                            <h5 className="mb-3">Amenities</h5>
                            {/* Amenities Analysis */}
                            <div className="mb-2"></div>
                            <TreeTable
                              value={nodes}
                              selectionMode="checkbox"
                              selectionKeys={selectedAmenities}
                              onSelectionChange={(e) => {
                                if (!isReadOnly) {
                                  setSelectedAmenities(
                                    e.value as {
                                      [key: string]: { checked: boolean };
                                    }
                                  );
                                }
                              }}
                              expandedKeys={expandedKeys}
                              onToggle={(e) => setExpandedKeys(e.value)} // always allow expand/collapse
                              tableStyle={{ minWidth: "100%", width: "100%" }}
                            >
                              <Column
                                field="label"
                                header="Amenity"
                                expander
                                body={(node) => (
                                  <span className="ms-2">{node.label}</span>
                                )}
                              />
                            </TreeTable>
                            <h5 className="mt-5 mb-3">Facilities</h5>
                            <TreeTable
                              value={facilityNodes}
                              selectionMode="checkbox"
                              selectionKeys={selectedFacilities}
                              onSelectionChange={(e) => {
                                if (!isReadOnly) {
                                  setSelectedFacilities(
                                    e.value as {
                                      [key: string]: { checked: boolean };
                                    }
                                  );
                                }
                              }}
                              tableStyle={{ minWidth: "100%", width: "100%" }}
                            >
                              <Column
                                field="label"
                                header="Facility"
                                expander // ✅ This is required to show checkbox!
                                body={(node) => (
                                  <span className="ms-2">{node.label}</span>
                                )}
                              />
                            </TreeTable>
                          </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                          {userRole === "Administrator" && (
                            <Button variant="success" type="submit">
                              {propertyId ? "Update" : "Save"}
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            type="button"
                            onClick={handleCancel}
                          >
                            {/* Cancel */}
                            {userRole === "Administrator" ? "Cancel" : "Close"}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEditProperty;
